import {t, Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {
  createFreeformQuestion,
  createSelectionOption,
  createSelectionQuestion,
} from '../../../../helpers/models/schedulingLinkHelpers';
import {
  isFreeformQuestion,
  isSelectionQuestion,
  SchedulingLinkFreeformQuestionModel,
  SchedulingLinkQuestionModel,
  SchedulingLinkQuestionTypesEnum,
  SchedulingLinkSelectionOptionModel,
  SchedulingLinkSelectionQuestionModel,
} from '../../../../models/api/schedulingLinkQuestionModel';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {SegmentedFilters} from '../../../common/segmentedFilters/segmentedFilters';
import {SchedulingLinkBoxedInputWrapper} from './schedulingLinkBoxedInputWrapper';

/*
 * Props.
 */

interface SchedulingLinkQuestionProps {
  question: SchedulingLinkQuestionModel;
  onChange: HandlerOf<SchedulingLinkQuestionModel>;
  onRemove: Handler;
  invalidQuestionOptionUids: ReadonlyArray<string>;
  setInvalidQuestionOptionUids: HandlerOf<ReadonlyArray<string>>;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  border: 1px solid ${(p) => p.theme.greys.shade30};
  border-radius: 8px;
`;

const StyledQuestionButtonsWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: auto min-content;
`;

const StyledFiltersWrapperDiv = styled.div`
  padding: 16px 0px 12px 16px;
  width: 168px;
`;

const StyledIconButton = styled(IconButton)`
  margin: 5px 5px 0px 0px;
`;

const StyledFilterItem = styled.div`
  padding: 0px 10px;
`;

const StyledQuestionContentWrapperDiv = styled.div`
  padding: 0px 16px 16px 16px;
`;

const StyledSelectionContentWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 10px;
`;

const StyledAddOptionButtonWrapperDiv = styled.div`
  display: flex;
`;

const StyledOptionRowWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: auto min-content;
`;

const StyledOptionErrorDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.palette.red.shade40};
  margin-top: 5px;
`;

/*
 * Component.
 */

export const SchedulingLinkQuestion: FC<SchedulingLinkQuestionProps> = (props) => {
  const theme = useUserTheme();
  const localize = useLocalize();
  const [freeformQuestion, setFreeformQuestion] = useState<SchedulingLinkFreeformQuestionModel>(
    isFreeformQuestion(props.question) ? props.question : createFreeformQuestion(props.question.uid),
  );
  const [selectionQuestion, setSelectionQuestion] = useState<SchedulingLinkSelectionQuestionModel>(
    isSelectionQuestion(props.question) ? props.question : createSelectionQuestion(props.question.uid),
  );

  const onQuestionChange = (question: SchedulingLinkQuestionModel) => {
    // Update local state
    if (isFreeformQuestion(question)) {
      setFreeformQuestion(question);
    } else {
      setSelectionQuestion(question);
    }

    // Update parent state
    props.onChange(question);
  };

  const filters = [
    {
      id: SchedulingLinkQuestionTypesEnum.FREEFORM,
      content: (
        <StyledFilterItem>
          <Trans id="scheduling-settings-freeform">Freeform</Trans>
        </StyledFilterItem>
      ),
      onClick: () => props.onChange(freeformQuestion),
    },
    {
      id: SchedulingLinkQuestionTypesEnum.SELECTION,
      content: (
        <StyledFilterItem>
          <Trans id="scheduling-settings-selection">Selection</Trans>
        </StyledFilterItem>
      ),
      onClick: () => props.onChange(selectionQuestion),
    },
  ];

  return (
    <StyledWrapperDiv>
      <StyledQuestionButtonsWrapperDiv>
        <StyledFiltersWrapperDiv>
          <SegmentedFilters
            filters={filters}
            selectedFilterIndex={filters.findIndex((filter) => filter.id === props.question.type)}
            color={theme.greys.shade70}
          />
        </StyledFiltersWrapperDiv>
        <StyledIconButton
          label={<Trans id="schedulinglink-settings-question-remove">Remove</Trans>}
          iconName="closeSmall"
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
          backgroundHoverColor={theme.alphas.gray10}
          onClick={props.onRemove}
        />
      </StyledQuestionButtonsWrapperDiv>
      <StyledQuestionContentWrapperDiv>
        {renderQuestion(localize, props, onQuestionChange)}
      </StyledQuestionContentWrapperDiv>
    </StyledWrapperDiv>
  );
};

function renderQuestion(
  localize: Localizer,
  props: SchedulingLinkQuestionProps,
  onQuestionChange: HandlerOf<SchedulingLinkQuestionModel>,
) {
  const {question} = props;

  return isFreeformQuestion(question)
    ? renderFreeformQuestion(localize, question, onQuestionChange)
    : renderSelectionQuestion(localize, props, question, onQuestionChange);
}

function renderFreeformQuestion(
  localize: Localizer,
  question: SchedulingLinkFreeformQuestionModel,
  onChange: HandlerOf<SchedulingLinkQuestionModel>,
) {
  return (
    <SchedulingLinkBoxedInputWrapper>
      <TextBox
        placeholder={localize(t('schedulinglink-meeting-question-placeholder')`What's your question?`)}
        defaultValue={question.question}
        onChange={(value) =>
          onChange({
            ...question,
            question: value,
          })
        }
      />
    </SchedulingLinkBoxedInputWrapper>
  );
}

function renderSelectionQuestion(
  localize: Localizer,
  props: SchedulingLinkQuestionProps,
  question: SchedulingLinkSelectionQuestionModel,
  onChange: HandlerOf<SchedulingLinkQuestionModel>,
) {
  const onUpdateOption = (newOption: SchedulingLinkSelectionOptionModel) =>
    onChange({
      ...question,
      selectionOptions: question.selectionOptions.map((otherOption) =>
        otherOption.uid === newOption.uid ? newOption : otherOption,
      ),
    });

  const onRemoveOption = (option: SchedulingLinkSelectionOptionModel) =>
    onChange({
      ...question,
      selectionOptions: question.selectionOptions.filter((otherOption) => otherOption.uid !== option.uid),
    });

  return (
    <StyledSelectionContentWrapperDiv>
      <SchedulingLinkBoxedInputWrapper>
        <TextBox
          placeholder={localize(t('schedulinglink-meeting-question-placeholder')`What's your question?`)}
          defaultValue={question.question}
          onChange={(value) =>
            onChange({
              ...question,
              question: value,
            })
          }
        />
      </SchedulingLinkBoxedInputWrapper>
      {question.selectionOptions.map((option, index) =>
        renderSelectionOption(
          localize,
          option,
          index,
          onUpdateOption,
          question.selectionOptions.length > 2 ? onRemoveOption : undefined,
          props.invalidQuestionOptionUids,
          props.setInvalidQuestionOptionUids,
        ),
      )}
      <StyledAddOptionButtonWrapperDiv>
        <Button
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.LINK}
          onClick={() =>
            onChange({...question, selectionOptions: [...question.selectionOptions, createSelectionOption()]})
          }
        >
          <Trans id="schedulinglink-settings-addoption">+ Add option</Trans>
        </Button>
      </StyledAddOptionButtonWrapperDiv>
    </StyledSelectionContentWrapperDiv>
  );
}

function renderSelectionOption(
  localize: Localizer,
  option: SchedulingLinkSelectionOptionModel,
  index: number,
  onUpdate: HandlerOf<SchedulingLinkSelectionOptionModel>,
  onRemove: HandlerOf<SchedulingLinkSelectionOptionModel> | undefined,
  invalidQuestionOptionUids: ReadonlyArray<string>,
  setInvalidQuestionOptionUids: HandlerOf<ReadonlyArray<string>>,
) {
  const isInvalid = invalidQuestionOptionUids.includes(option.uid);
  const onChange = (value: string) => {
    onUpdate({...option, value});
    if (isInvalid) {
      setInvalidQuestionOptionUids(invalidQuestionOptionUids.filter((uid) => uid !== option.uid));
    }
  };
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledOptionRowWrapperDiv key={option.uid}>
          <SchedulingLinkBoxedInputWrapper>
            <TextBox
              placeholder={localize(
                t('schedulinglink-meeting-questionoption-placeholder')`Option ${index + 1}`,
              )}
              defaultValue={option.value}
              onChange={onChange}
              hasError={isInvalid}
              errorMessage={
                isInvalid && (
                  <StyledOptionErrorDiv>
                    <Trans id="schedulinglink-settings-invalidoptionmessage">Enter option</Trans>
                  </StyledOptionErrorDiv>
                )
              }
            />
          </SchedulingLinkBoxedInputWrapper>
          {onRemove && (
            <IconButton
              iconName="clearSmall"
              label={<Trans id="schedulinglink-questionoption-remove">Remove</Trans>}
              onClick={() => onRemove(option)}
              backgroundHoverColor={theme.alphas.transparent}
            />
          )}
        </StyledOptionRowWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}
